/**
 * TodayWidget Component
 * Compact widget showing today's tasks on the dashboard
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle2, Circle, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '@/hooks/useTasks';
import { prioritiseTasks } from '@/services/finTasksAIService';
import { cn, formatCurrency } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import Tooltip from '@/components/common/Tooltip';

export default function TodayWidget() {
  const navigate = useNavigate();
  const { currency: userCurrency, user } = useAuth();
  const currency = userCurrency || 'USD';
  const { tasks, loading, toggleTask, refetch } = useTasks(null, 'today');
  const [aiFocus, setAiFocus] = useState(null); // { taskId, reason }
  const [aiFocusLoading, setAiFocusLoading] = useState(false);
  const lastFetchRef = useRef(0); // Track last API call time for debouncing
  const fetchTimeoutRef = useRef(null); // Debounce timer

  const summaries = useMemo(() => tasks.map(t => ({ id: t.id, title: t.title, priority: t.priority, isFinancialTask: t.isFinancialTask, dueDate: t.dueDate || null })), [tasks]);

  // Create a stable key for summaries to prevent excessive re-fetching
  const summariesKey = useMemo(() => {
    return summaries.map(s => s.id).sort().join(',');
  }, [summaries]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    
    async function fetchFocus() {
      // Need both user and tasks to make the API call
      if (!user?.uid || summaries.length === 0) { setAiFocus(null); return; }
      
      // Debounce: Don't call API more than once every 30 seconds
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchRef.current;
      const DEBOUNCE_MS = 30000; // 30 seconds
      
      if (timeSinceLastFetch < DEBOUNCE_MS && lastFetchRef.current > 0) {
        console.log('[TodayWidget] Skipping AI focus - debounced');
        return;
      }
      
      setAiFocusLoading(true);
      try {
        console.log('[AI-HOOK] TodayWidget', { action: 'focus-suggestion', taskCount: summaries.length });
        lastFetchRef.current = Date.now();
        const data = await prioritiseTasks({ userId: user.uid, tasks: summaries, timeWindow: 'today' });
        // Set the top priority task as focus
        if (!cancelled && data?.ordered?.length > 0) {
          setAiFocus({ taskId: data.ordered[0].taskId, reason: data.ordered[0].reason });
        }
      } catch (e) {
        console.error('[TodayWidget] AI focus error:', e);
      } finally {
        if (!cancelled) setAiFocusLoading(false);
      }
    }
    
    // Clear existing timeout and debounce the fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Debounce the API call by 2 seconds to let tasks settle
    fetchTimeoutRef.current = setTimeout(() => {
      fetchFocus();
    }, 2000);
    
    return () => { cancelled = true; };
  }, [summariesKey, user?.uid]); // Use stable key instead of full summaries array

  const handleToggle = async (e, task) => {
    e.stopPropagation();
    try {
      await toggleTask(task.listId, task.id);
      refetch();
    } catch (error) {
      console.error('Failed to toggle task:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-5 hover:border-[#006D77] transition-all"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#118AB2]/10 flex items-center justify-center">
            <Calendar size={20} className="text-[#118AB2]" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Today's Tasks</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {loading ? '...' : `${tasks.length} task${tasks.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/tasks')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
          aria-label="View all tasks"
        >
          <ChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-[#006D77]" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle2 size={48} className="mx-auto text-[#06D6A0] mb-2" />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            All caught up for today! 🎉
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {tasks.slice(0, 5).map((task, index) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate('/tasks')}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer group transition-colors"
            >
              <Tooltip
                label={
                  task.status === 'done'
                    ? 'Mark this task as incomplete and bring it back to your queue.'
                    : 'Mark this task as done and keep your shopping on track.'
                }
              >
                <button
                  onClick={(e) => handleToggle(e, task)}
                  className="flex-shrink-0"
                  aria-label={task.status === 'done' ? 'Mark as incomplete' : 'Mark as complete'}
                >
                  {task.status === 'done' ? (
                    <CheckCircle2 size={20} className="text-[#06D6A0] fill-current" />
                  ) : (
                    <Circle size={20} className="text-gray-400 dark:text-gray-500 group-hover:text-[#006D77]" />
                  )}
                </button>
              </Tooltip>
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    'text-sm font-semibold text-gray-900 dark:text-white truncate',
                    task.status === 'done' && 'line-through text-gray-500 dark:text-gray-400'
                  )}
                >
                  {task.title}
                </p>
                {task.isFinancialTask && task.estimatedAmount && (
                  <p className="text-xs text-amber-700 font-medium">
                    {formatCurrency(task.estimatedAmount, currency)}
                  </p>
                )}
              </div>
              {task.priority === 'high' && (
                <span className="flex-shrink-0 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </motion.div>
          ))}
          {tasks.length > 5 && (
            <button
              onClick={() => navigate('/tasks')}
              className="w-full py-2 text-sm font-semibold text-[#006D77] hover:bg-[#006D77]/5 rounded-xl transition-colors"
            >
              View {tasks.length - 5} more task{tasks.length - 5 !== 1 ? 's' : ''}
            </button>
          )}
          {aiFocus && (
            <div className="mt-2 p-3 rounded-xl bg-[#006D77]/5 border border-[#006D77]/30">
              <p className="text-xs font-semibold text-[#006D77] flex items-center gap-1">
                <Calendar size={12} /> AI Focus Suggestion
              </p>
              <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">Finish <span className="font-semibold">{aiFocus.title}</span> first to stay on track.</p>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
